import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Mail, Phone, MapPin, Megaphone, Users, Globe2, TrendingUp, Send, ChevronRight, Home as HomeIcon } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { submitAdInquiry } from '../services/newsService';

const PACKAGES = [
  { name: 'Local Spotlight' },
  { name: 'Statewide Reach' },
  { name: 'Global Premier' },
];

export default function Contact() {
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', pkg: '', budget: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error('Please fill in your name, email and message.');
      return;
    }
    setSubmitting(true);
    try {
      await submitAdInquiry(form);
      toast.success('Thank you! Our advertising team will contact you within 1 business day.');
      setForm({ name: '', company: '', email: '', phone: '', pkg: '', budget: '', message: '' });
    } catch {
      toast.error('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Hero */}
      <div className="text-white" style={{ background: 'linear-gradient(120deg, #0b3d26 0%, #0f5132 50%, #157347 100%)' }}>
        <div className="max-w-[1320px] mx-auto px-4 py-14">
          <nav className="flex items-center gap-1.5 text-[13px] text-[#a9d4bd] mb-5">
            <Link to="/" className="inline-flex items-center gap-1 hover:text-white"><HomeIcon className="w-3.5 h-3.5" />Home</Link>
            <ChevronRight className="w-3.5 h-3.5" /><span className="text-white font-semibold">Advertise &amp; Contact</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-3 py-1 text-[13px] font-medium text-[#cdeddb] mb-4">
            <Megaphone className="w-4 h-4" /> Advertise with TMB Global News
          </div>
          <h1 className="font-display font-extrabold text-4xl sm:text-5xl leading-tight max-w-3xl">Reach over 5 million people, locally &amp; globally</h1>
          <p className="text-[#cdeddb] text-lg mt-4 max-w-2xl">Put your brand in front of Michigan’s most engaged news audience. Tell us your goals and our team will build the perfect campaign for you.</p>
          <div className="grid grid-cols-3 gap-4 mt-8 max-w-2xl">
            {[{ i: Users, n: '5M+', l: 'Monthly readers' }, { i: Globe2, n: '120+', l: 'Countries reached' }, { i: TrendingUp, n: '2x', l: 'Daily updates' }].map((s, idx) => (
              <div key={idx} className="bg-white/10 border border-white/15 rounded-xl p-4">
                <s.i className="w-5 h-5 text-[#8be0b0] mb-2" />
                <div className="text-2xl font-extrabold">{s.n}</div>
                <div className="text-[12px] text-[#a9d4bd]">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-[1320px] mx-auto px-4 py-12">
        {/* Form + info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 rounded-2xl border border-[#e3ece7] bg-white shadow-sm p-6 sm:p-8">
            <h2 className="font-display font-bold text-2xl text-[#0b3d26]">Tell us about your campaign</h2>
            <p className="text-[#5b6b63] text-sm mt-1">Fill in the form and our advertising team will reach out with a tailored proposal.</p>
            <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
              <Field label="Full name *" value={form.name} onChange={set('name')} placeholder="Jane Doe" />
              <Field label="Company" value={form.company} onChange={set('company')} placeholder="Acme Inc." />
              <Field label="Email *" type="email" value={form.email} onChange={set('email')} placeholder="jane@company.com" />
              <Field label="Phone" value={form.phone} onChange={set('phone')} placeholder="(313) 555-0100" />
              <div>
                <label className="block text-sm font-semibold text-[#14241d] mb-1.5">Package of interest</label>
                <Select value={form.pkg} onValueChange={(v) => setForm((f) => ({ ...f, pkg: v }))}>
                  <SelectTrigger className="w-full border-[#cfe3d8]"><SelectValue placeholder="Select a package" /></SelectTrigger>
                  <SelectContent>
                    {PACKAGES.map((p) => (<SelectItem key={p.name} value={p.name}>{p.name}</SelectItem>))}
                    <SelectItem value="Custom">Custom / Not sure yet</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Field label="Monthly budget" value={form.budget} onChange={set('budget')} placeholder="e.g. $1,500" />
              <div className="sm:col-span-2">
                <label className="block text-sm font-semibold text-[#14241d] mb-1.5">Your advertising requirements *</label>
                <textarea value={form.message} onChange={set('message')} rows={5} placeholder="Tell us about your goals, target audience, and any specific placements you'd like..." className="w-full px-3 py-2.5 rounded-lg border border-[#cfe3d8] focus:border-[#2e9e6b] outline-none text-sm resize-none" />
              </div>
              <div className="sm:col-span-2">
                <button type="submit" disabled={submitting} className="inline-flex items-center gap-2 bg-[#157347] hover:bg-[#0f5132] disabled:opacity-60 text-white font-semibold px-6 py-3 rounded-lg transition-colors">
                  <Send className="w-4 h-4" /> {submitting ? 'Sending\u2026' : 'Submit Advertising Request'}
                </button>
              </div>
            </form>
          </div>

          <div className="space-y-5">
            <div className="rounded-2xl border border-[#e3ece7] bg-[#eef8f2] p-6">
              <h3 className="font-display font-bold text-lg text-[#0b3d26] mb-4">Get in touch</h3>
              <ul className="space-y-4 text-sm">
                <li className="flex items-start gap-3"><span className="w-9 h-9 rounded-full bg-[#157347] text-white flex items-center justify-center shrink-0"><Mail className="w-4 h-4" /></span><span><span className="block font-semibold text-[#14241d]">Email</span><a href="mailto:info@tmbglobalnews.com" className="text-[#157347]">info@tmbglobalnews.com</a></span></li>
                <li className="flex items-start gap-3"><span className="w-9 h-9 rounded-full bg-[#157347] text-white flex items-center justify-center shrink-0"><Phone className="w-4 h-4" /></span><span><span className="block font-semibold text-[#14241d]">Phone</span><a href="tel:+19899922600" className="text-[#5b6b63]">+1 989.992.2600</a></span></li>
                <li className="flex items-start gap-3"><span className="w-9 h-9 rounded-full bg-[#157347] text-white flex items-center justify-center shrink-0"><MapPin className="w-4 h-4" /></span><span><span className="block font-semibold text-[#14241d]">Office</span><span className="text-[#5b6b63]">301 E Genesee Ave, Suite 201-B, Saginaw, MI 48607, USA</span></span></li>
              </ul>
            </div>
            <div className="rounded-2xl overflow-hidden border border-[#e3ece7]">
              <div className="p-5 text-white text-center" style={{ background: 'linear-gradient(135deg, #157347, #0b3d26)' }}>
                <Megaphone className="w-8 h-8 mx-auto mb-2 text-[#8be0b0]" />
                <p className="font-semibold">Prime placements sell out fast.</p>
                <p className="text-[13px] text-[#cdeddb] mt-1">Secure your spot for the next quarter today.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text' }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-[#14241d] mb-1.5">{label}</label>
      <input type={type} value={value} onChange={onChange} placeholder={placeholder} className="w-full px-3 py-2.5 rounded-lg border border-[#cfe3d8] focus:border-[#2e9e6b] outline-none text-sm" />
    </div>
  );
}
